import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../../config/firebase"; // Adjust the path as needed
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  CircularProgress,
  Box,
  TextField,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import AddForm from "../AddForm/AddForm";
import EditModal from "../EditForm/EditModal";
import EditAvatar from "../EditForm/Avatar";
import { deleteUser } from "../../../../config/firebase";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const AdminUsers = () => {
  const [ccpAdminUsers, setCcpAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ccpSearch, setCcpSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCcpAdminUsers = async () => {
    try {
      console.log("Fetching CCP Admin users...");
      const ccpAdminQuery = query(
        collection(db, "users"),
        where("ccpAdminFlag", "==", true),
        orderBy("name", "asc")
      );

      const ccpAdminSnapshot = await getDocs(ccpAdminQuery);

      if (ccpAdminSnapshot.empty) {
        console.log("No matching CCP Admin documents.");
      } else {
        const ccpAdminUsers = ccpAdminSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log("Fetched CCP Admin users:", ccpAdminUsers);
        setCcpAdminUsers(ccpAdminUsers);
      }
    } catch (error) {
      console.error("Error fetching CCP Admin users: ", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchCcpAdminUsers();
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleCcpSearchChange = (event) => {
    setCcpSearch(event.target.value);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleEditSuccess = () => {
    fetchCcpAdminUsers();
    setSnackbarMessage("User has been updated");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);

    try {
      const userRef = doc(db, "users", userToDelete.id);
      await deleteDoc(userRef);

      await deleteUser(userToDelete.id); // Assuming deleteUser function takes user ID as parameter

      setSnackbarMessage("User has been deleted");
      setSnackbarOpen(true);

      // Refresh the user lists after deletion
      fetchCcpAdminUsers();
    } catch (error) {
      console.error("Error deleting user: ", error);
      setSnackbarMessage("Error deleting user");
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleAvatarUploadSuccess = () => {
    fetchCcpAdminUsers();
  };

  const filteredCcpAdminUsers = ccpAdminUsers.filter((user) =>
    user.name.toLowerCase().includes(ccpSearch.toLowerCase())
  );

  return (
    <Box>
      <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
        <AddForm onSuccess={handleEditSuccess} />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" component="div" gutterBottom>
          CCP Admin Users
        </Typography>
        <TextField
          label="Search CCP Admin Users"
          variant="outlined"
          size="small"
          value={ccpSearch}
          onChange={handleCcpSearchChange}
        />
      </Box>
      <Divider style={{ marginBottom: "20px" }} />
      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {filteredCcpAdminUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Avatar
                        src={user.avatar}
                        alt={user.name}
                        sx={{ width: 80, height: 80 }}
                        onAvatarUploadSuccess={handleAvatarUploadSuccess}
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant="h6" component="div">
                        {user.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.phone}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          style={{ color: "purple", cursor: "pointer" }}
                          size="2x"
                          onClick={() => handleEditClick(user)}
                        />
                        <FontAwesomeIcon
                          icon={faTrash}
                          style={{
                            color: "red",
                            cursor: "pointer",
                            marginLeft: "10px",
                            marginRight: "10px",
                          }}
                          size="2x"
                          onClick={() => handleDeleteClick(user)}
                        />
                        <EditAvatar
                          user={user}
                          onAvatarUploadSuccess={handleAvatarUploadSuccess}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      {selectedUser && (
        <EditModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          user={selectedUser}
          onSuccess={handleEditSuccess}
        />
      )}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        deleting={deleteLoading}
        title="Confirm Delete"
        message="Are you sure you want to delete this user?"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%", backgroundColor: "black", color: "white" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
